/**
 * EditTextCell - Reusable text input for DataGrid inline editing
 *
 * Commits value on Enter or blur (not on every keystroke).
 *
 * Usage in column definition:
 * ```tsx
 * {
 *   field: 'website',
 *   editable: true,
 *   renderEditCell: (params) => <EditTextCell params={params} placeholder="Enter URL..." />,
 * }
 * ```
 */

import { InputBase } from '@mui/material'
import { GridRenderEditCellParams, useGridApiContext } from '@mui/x-data-grid-premium'
import { useCallback, useEffect, useRef, useState } from 'react'

interface EditTextCellProps {
  params: GridRenderEditCellParams
  placeholder?: string
  /** Input type (text, url, email, etc.) */
  type?: string
}

export const EditTextCell = ({ params, placeholder = '', type = 'text' }: EditTextCellProps) => {
  const apiRef = useGridApiContext()
  const inputRef = useRef<HTMLInputElement>(null)
  const [value, setValue] = useState(params.value ?? '')
  const hasCommittedRef = useRef(false)

  // Focus and select all text when cell enters edit mode
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [])

  const commitValue = useCallback(() => {
    if (hasCommittedRef.current) return
    hasCommittedRef.current = true

    apiRef.current.setEditCellValue({
      id: params.id,
      field: params.field,
      value: value,
    })

    apiRef.current.stopCellEditMode({ id: params.id, field: params.field })
  }, [apiRef, params.id, params.field, value])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        commitValue()
      } else if (event.key === 'Escape') {
        hasCommittedRef.current = true
        apiRef.current.stopCellEditMode({ id: params.id, field: params.field, ignoreModifications: true })
      } else if (event.key === 'Tab') {
        commitValue()
      }
    },
    [apiRef, params.id, params.field, commitValue]
  )

  const handleBlur = useCallback(() => {
    // Small delay to allow click events on other cells
    setTimeout(() => {
      commitValue()
    }, 100)
  }, [commitValue])

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value)
  }, [])

  return (
    <InputBase
      inputRef={inputRef}
      value={value}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      placeholder={placeholder}
      type={type}
      fullWidth
      sx={{
        height: '100%',
        fontSize: 'inherit',
        px: 1,
        '& .MuiInputBase-input': {
          padding: 0,
        },
      }}
    />
  )
}

export default EditTextCell
